import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_ALERT = { trim: "RAV4 LE FWD", price: "$32,899.00", region: "Texas", enabled: true };

const HERO_IMG = "/images/hero.jpg";

const MaskedLine = ({ children, delay, className = "" }) => (
    <span className={`block overflow-hidden ${className}`}>
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const STATS = [
    { value: "$399", label: "One-time membership" },
    { value: "90 days", label: "Guaranteed allocation — or full refund" },
    { value: "Below TSRP", label: "Even including our fee" },
];

const VALUE_BULLETS = [
    "Professionally negotiated pricing below Total Suggested Retail Price — including the $399",
    "No Dealer, Distributor, or Local Installed Options — preserving residual value and lowering your price",
    "Financing advisory — eliminating interest rate mark-ups",
];

export const Hero = () => {
    const ref = useRef(null);
    const [alert, setAlert] = useState(DEFAULT_ALERT);

    useEffect(() => {
        axios
            .get(`${API}/deal-alert`)
            .then((r) => setAlert(r.data))
            .catch(() => {});
    }, []);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
    const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="noise-overlay relative flex min-h-screen flex-col justify-end overflow-hidden"
        >
            <motion.div style={{ y: bgY }} className="absolute inset-0">
                <motion.img
                    src={HERO_IMG}
                    alt="2026 Toyota RAV4 in a moody forest setting"
                    className="h-[120%] w-full object-cover"
                    initial={{ scale: 1.18 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
                />
                <div className="absolute inset-0 bg-black/55" />
                <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-[#050505] to-transparent" />
            </motion.div>

            <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-20 pt-40">
                {alert.enabled && (
                    <motion.div
                        data-testid="deal-alert"
                        initial={{ opacity: 0, y: -16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        className="mb-8 flex justify-center"
                    >
                        <div className="border border-[#2563EB] bg-black/70 px-6 py-4 text-center backdrop-blur-xl">
                            <p className="font-display text-sm font-extrabold uppercase tracking-[0.15em] text-white sm:text-base">
                                <span className="text-[#2563EB]">Deal alert:</span> Now
                                allocating {alert.trim} at {alert.price}
                            </p>
                            <p className="mt-1 text-xs font-bold uppercase tracking-[0.25em] text-zinc-400">
                                Region: {alert.region}
                            </p>
                            <button
                                data-testid="deal-alert-reserve-button"
                                onClick={() => scrollToId("join")}
                                className="mt-3 text-xs font-bold uppercase tracking-[0.25em] text-[#2563EB] underline underline-offset-4 transition-colors duration-300 hover:text-white"
                            >
                                Reserve your 2026 RAV4 today →
                            </button>
                        </div>
                    </motion.div>
                )}

                <motion.div
                    initial={{ opacity: 0, y: -16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="mb-12 flex justify-center"
                >
                    <img
                        src="/images/logo.png"
                        alt="RAV4 Buyers Club logo — 2026 RAV4 front view"
                        data-testid="hero-logo"
                        className="w-[240px] bg-white p-4 sm:w-[300px]"
                    />
                </motion.div>

                <ul data-testid="hero-value-bullets" className="mb-10 space-y-3">
                    {VALUE_BULLETS.map((bullet, i) => (
                        <motion.li
                            key={bullet}
                            initial={{ opacity: 0, x: -24 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.7, delay: 0.15 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                            className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.15em] text-zinc-200 sm:text-sm"
                        >
                            <span className="h-2 w-2 shrink-0 rotate-45 bg-[#2563EB]" aria-hidden="true" />
                            {bullet}
                        </motion.li>
                    ))}
                </ul>

                <motion.p
                    data-testid="hero-eyebrow"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.65 }}
                    className="mb-8 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.35em] text-zinc-300"
                >
                    <span className="h-2 w-2 rotate-45 bg-[#2563EB]" aria-hidden="true" />
                    Members-only allocation — 2026 model year
                </motion.p>

                <h1
                    data-testid="hero-headline"
                    className="font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl"
                >
                    <MaskedLine delay={0.35}>Your 2026 RAV4.</MaskedLine>
                    <MaskedLine delay={0.5} className="text-[#2563EB]">
                        Allocated. Guaranteed.
                    </MaskedLine>
                    <MaskedLine delay={0.65}>For $399.</MaskedLine>
                </h1>

                <motion.p
                    data-testid="hero-subheadline"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.95 }}
                    className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg"
                >
                    The RAV4 Buyers Club reserves a factory-fresh 2026 Toyota RAV4 allocation in
                    your name — before public waitlists even open. Guaranteed below Total
                    Suggested Retail Price, including our fee.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.1 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <button
                        data-testid="hero-join-button"
                        onClick={() => scrollToId("join")}
                        className="group flex items-center gap-3 bg-[#2563EB] px-8 py-4 text-sm font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
                    >
                        Claim your allocation
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </button>
                    <button
                        data-testid="hero-how-it-works-button"
                        onClick={() => scrollToId("how-it-works")}
                        className="flex items-center gap-3 border border-white/25 px-8 py-4 text-sm font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-white hover:bg-white/10"
                    >
                        How it works
                        <ArrowDown className="h-4 w-4" />
                    </button>
                </motion.div>

                <motion.div
                    data-testid="hero-stats"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.3 }}
                    className="mt-16 grid grid-cols-1 gap-6 border-t border-white/15 pt-8 sm:grid-cols-3"
                >
                    {STATS.map((s) => (
                        <div key={s.label}>
                            <p className="font-display text-2xl font-bold tracking-tight">
                                {s.value}
                            </p>
                            <p className="mt-1 text-xs uppercase tracking-[0.2em] text-zinc-400">
                                {s.label}
                            </p>
                        </div>
                    ))}
                </motion.div>
            </motion.div>
        </section>
    );
};
